import { get } from 'lodash';
import React, { useMemo } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { Flex, Radio } from 'antd';
import { change, Field, type WrappedFieldProps } from 'redux-form';

import t from 'common/utils/translate';
import { STORY_TYPE } from 'common/constants';
import { getBrandKitCreationDefaults } from 'common/brand-kit/projection';
import { IS_PROD } from 'common/utils/environment';

import { AdminThunkDispatch, useAdminSelector } from 'admin/reducers';
import {
	isSuperUserSelector,
	maxWidgetPageviewsSelector,
	selectActiveOrganization,
} from 'admin/reducers/user/selectors';
import { LANG_OPTIONS } from 'admin/constants/common';
import { Select } from 'admin/components/common/Form';
import { Label } from 'admin/components/common/Form/Label';
import BrandKitSelect from 'admin/components/common/BrandKitSelect';
import Button from 'admin/components/common/Button';
import { Icon } from 'admin/components/common/Icon';
import Text from 'admin/components/common/Text';
import type { StoredBrandKit } from 'common/brand-kit/types';

import { fields, FormValues } from './Form';
import WizardTitle from './WizardTitle';
import css from './CreateStoryModal.scss';

const cloneStoryType = 'clone' as const;
type CloneStoryType = typeof cloneStoryType;

type MainViewProps = {
	form: string;
	onOk: (type: FormValues['type'] | CloneStoryType) => void;
	dispatch: AdminThunkDispatch;
	values: FormValues;
};

const commonTypes = [STORY_TYPE.STANDALONE, STORY_TYPE.EMBED, STORY_TYPE.WIDGET];

type StoryTypes = (ArrayType<typeof commonTypes> | CloneStoryType)[];

type BrandKitFieldProps = WrappedFieldProps & {
	kits: StoredBrandKit[];
	onBrandKitChange: (brandKitId: string) => void;
};

function BrandKitField({ input, kits, onBrandKitChange }: BrandKitFieldProps) {
	return (
		<BrandKitSelect
			id="create-story-brand-kit"
			kits={kits}
			value={String(input.value ?? '')}
			includeNoBrandKit
			onChange={brandKitId => {
				input.onChange(brandKitId);
				onBrandKitChange(brandKitId);
			}}
		/>
	);
}

const TemplateType: React.FC<MainViewProps> = ({ values, dispatch, form, onOk }) => {
	const isSuperUser = useAdminSelector(isSuperUserSelector);
	const maxWidgetPageviews = useAdminSelector(maxWidgetPageviewsSelector);
	const organization = useAdminSelector(selectActiveOrganization);
	const types = useMemo(() => {
		let result: StoryTypes = [...commonTypes];

		if (maxWidgetPageviews === 0) {
			result = result.filter(type => type !== STORY_TYPE.WIDGET);
		}

		if (isSuperUser && !IS_PROD) {
			result = [...result, cloneStoryType];
		}

		return result;
	}, [isSuperUser, maxWidgetPageviews]);
	const type = get(values, fields.type);
	const brandKits = organization?.metadata?.brandKits ?? [];
	const organizationLanguage = (LANG_OPTIONS.find(option => option.value === organization?.metadata?.storyLang)
		?.value ?? LANG_OPTIONS[0].value) as FormValues['lang'];
	const handleBrandKitChange = (brandKitId: string) => {
		const defaults = getBrandKitCreationDefaults(brandKits, brandKitId || null, {
			language: organizationLanguage,
			favicon: organization?.favicon,
		});
		dispatch(change(form, fields.lang, defaults.language));
	};

	return (
		<Flex className={css.templateTypeView} vertical align="center">
			<WizardTitle>{t('createStoryModal.type.title')}</WizardTitle>
			<Radio.Group
				className={css.templateTypeChoicesGroup}
				value={type}
				onChange={event => dispatch(change(form, fields.type, event.target.value))}
			>
				<Flex className={css.templateTypeChoices} gap={12} justify="center" align="stretch" wrap>
					{types.map(value => (
						<Radio.Button className={css.templateTypeChoice} value={value} key={value}>
							{value === type ? <CheckCircleFilled className={css.templateTypeCheck} /> : null}
							<Icon
								type={value === cloneStoryType ? 'copy-text' : `sc-${value}`}
								className={css.templateTypeIcon}
								width={40}
								height={40}
								color={value === type ? 'var(--ra-color-brand-dark)' : 'currentColor'}
							/>
							<Text
								size={Text.size.body}
								weight={Text.weight.semibold}
								transform={Text.transform.capitalize}
							>
								{t(`createStoryModal.${fields.type}.${value}.title`)}
							</Text>
							<Text
								className={css.templateTypeDescription}
								size={Text.size.label}
								color={Text.color.blackMid}
							>
								{t(`createStoryModal.${fields.type}.${value}.desc`)}
							</Text>
						</Radio.Button>
					))}
				</Flex>
			</Radio.Group>

			<Flex className={css.templateTypeSettings} gap={16}>
				<Flex className={css.templateTypeSetting} vertical gap={6}>
					<Label htmlFor="create-story-brand-kit">Brand Kit</Label>
					<Field
						name={fields.brandKitId}
						component={BrandKitField}
						kits={brandKits}
						onBrandKitChange={handleBrandKitChange}
					/>
				</Flex>
				<Field
					name={fields.lang}
					component={Select}
					className={css.templateTypeSetting}
					label={{
						children: t(`createStoryModal.fields.${fields.lang}.label`),
						htmlFor: 'create-story-language',
					}}
					options={LANG_OPTIONS}
				/>
			</Flex>

			<Button
				className={css.templateTypeContinue}
				size="medium"
				disabled={!type}
				onClick={() => type && onOk(type)}
			>
				{t('createStoryModal.next')}
			</Button>
		</Flex>
	);
};

export default TemplateType;
